import { BaseModel } from './BaseModel';
import { BatchDivision } from './BatchDivision';
import { Airsacculitis, S0, E0, D0, RationEndPrediction } from './Documents';
import { ValueRanged } from './Rules';
import { Silo } from './Silo';
import { NutritionalPlan } from './NutritionalPlan';

class PcPProjection {
  constructor(data = {}) {
    this.age = data.age ?? null;
    this.weight = data.weight ?? null;
    this.projection = data.projection ?? null;
  }
}

class Sex {
  constructor(data = {}) {
    this.name = data.name ?? null;
    this.externalId = data.external_id ?? data.externalId ?? null;
  }
}

export class Mortality extends BaseModel {
  hydrate(data, source) {
    const isApi = source === 'api';

    this.age = data?.age ?? null;

    this.amountMortality = isApi ? data.amount_mortality : data.amountMortality ?? null;

    this.amountEliminated = isApi ? data.amount_eliminated : data.amountEliminated ?? null;
  }

  static fromApi(data) {
    return this.create(data, 'api');
  }

  static fromStore(data) {
    return this.create(data, 'store');
  }
}

export class Batch extends BaseModel {
  hydrate(data, source) {
    const isApi = source === 'api';

    this.id = data.id ?? null;
    this.identification = data.identification ?? null;
    this.lineage = data.lineage ?? null;

    this.sex = data.sex ? new Sex(data.sex) : null;

    this.age = data.age ?? null;

    this.realAge = isApi ? data.real_age : data.realAge ?? null;

    this.productionType = isApi ? data.production_type : data.productionType ?? null;

    this.currentLodge = isApi ? data.current_lodged : data.currentLodge ?? null;

    this.openingDate = isApi ? data.opening_date : data.openingDate ?? null;

    this.closingDate = isApi ? data.closing_date : data.closingDate ?? null;

    this.breedingType = isApi ? data.breeding_type : data.breedingType ?? null;

    this.startingAge = isApi ? data.starting_age : data.startingAge ?? null;

    this.amountLodged = isApi ? data.amount_lodged : data.amountLodged ?? null;

    const divisions = isApi ? data.batch_divisions : data.batchDivisions;

    this.batchDivisions = Array.isArray(divisions)
      ? divisions.map((d) => BatchDivision.create(d, source))
      : [];

    /* ===== Performance ===== */

    this.weight = data.weight ?? null;
    this.gain = data.gain ?? null;
    this.performance = data.performance ?? null;
    this.uniformity = data.uniformity ?? null;

    this.conversionFactor = isApi ? data.conversion_factor : data.conversionFactor ?? null;

    this.week = data.week ?? null;

    this.weeklyConsumedRation = isApi
      ? data.weekly_consumed_ration
      : data.weeklyConsumedRation ?? null;

    this.targetWeight = isApi ? data.target_weight : data.targetWeight ?? null;

    this.idealWeight = isApi ? data.ideal_weight : data.idealWeight ?? null;

    /* ===== Sanity ===== */

    this.airsacculitis = data.airsacculitis
      ? Airsacculitis.create(data.airsacculitis, source)
      : null;

    this.mortality = data.mortality ? Mortality.create(data.mortality, source) : null;

    this.s0 = data.s0 ? S0.create(data.s0, source) : null;

    this.d0 = data.d0 ? D0.create(data.d0, source) : null;

    this.e0 = data.e0 ? E0.create(data.e0, source) : null;

    this.environmentTemperature =
      data.environment_temperature || data.environmentTemperature
        ? ValueRanged.create(data.environment_temperature || data.environmentTemperature, source)
        : null;

    this.humidity = data.humidity ? ValueRanged.create(data.humidity, source) : null;

    this.co2 = data.co2 ? ValueRanged.create(data.co2, source) : null;

    this.dailyTempMin = data.daily_temp?.min ?? data.dailyTempMin ?? null;

    this.dailyTempMax = data.daily_temp?.max ?? data.dailyTempMax ?? null;

    /* ===== Slaughter ===== */

    this.entryWeight = isApi ? data.entry_weight : data.entryWeight ?? null;

    const projections = isApi ? data.pcp_projections : data.pcpProjections;

    this.pcpProjections = Array.isArray(projections)
      ? projections.map((p) => new PcPProjection(p))
      : [];

    const targetDate = isApi ? data.target_date : data.targetDate;

    this.targetDate = targetDate ? new Date(targetDate) : null;

    this.targetAge = isApi ? data.target_age : data.targetAge ?? null;

    this.predictedWeight = isApi ? data.predicted_weight : data.predictedWeight ?? null;

    /* ===== Nutrition ===== */

    this.averageConsumption = isApi ? data.average_consumption : data.averageConsumption ?? null;

    const silos = isApi ? data.silos : data.silos;

    this.silos = Array.isArray(silos) ? silos.map((s) => Silo.create(s, source)) : [];

    this.consumptionHistory = data.consumptionHistory || [];

    const rationPrediction = isApi ? data.ration_end_prediction : data.rationEndPrediction;

    this.rationEndPrediction = Array.isArray(rationPrediction)
      ? rationPrediction.map((r) => RationEndPrediction.create(r, source))
      : [];

    /* == Nutritional Plan == */

    const nutritionPlan = isApi ? data.nutrition_plan : data.nutritionPlan;

    this.nutritionPlan = NutritionalPlan.create(nutritionPlan, source);
  }

  /* ===== Factories ===== */

  static fromApi(data) {
    return this.create(data, 'api');
  }

  static fromStore(data) {
    return this.create(data, 'store');
  }
}
